/**
 * Card Component
 *
 * A flexible card component for displaying content with various styles and layouts.
 * Supports different variants, padding options, and interactive states.
 */

import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'filled' | 'ghost';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type CardSize = 'sm' | 'md' | 'lg' | 'xl';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  size?: CardSize;
  interactive?: boolean;
  hoverable?: boolean;
  children: React.ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      size = 'md',
      interactive = false,
      hoverable = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Base card classes - Exact HTML specifications
    const baseClasses = cn(
      'rounded-lg border transition-all duration-200',
      'shadow-sm hover:shadow-md',
      'bg-white dark:bg-gray-800',
      'border-gray-200 dark:border-gray-700',
      interactive && 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500',
      hoverable && 'hover:shadow-lg hover:-translate-y-0.5'
    );

    // Variant classes
    const variantClasses = {
      default: cn('bg-white border-gray-200 shadow-sm', 'dark:bg-gray-800 dark:border-gray-700'),
      elevated: cn('bg-white border-gray-200 shadow-lg', 'dark:bg-gray-800 dark:border-gray-700'),
      outlined: cn('bg-transparent border-2 border-gray-300', 'dark:border-gray-600'),
      filled: cn('bg-gray-50 border-gray-200', 'dark:bg-gray-700 dark:border-gray-600'),
      ghost: cn('bg-transparent border-0 shadow-none', 'hover:bg-gray-50 dark:hover:bg-gray-800'),
    };

    // Padding classes
    const paddingClasses = {
      none: 'p-0',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
      xl: 'p-10',
    };

    // Size classes (affects max-width)
    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
    };

    return (
      <div
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          paddingClasses[padding],
          sizeClasses[size],
          className
        )}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card sub-components
export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'text-lg font-semibold leading-none tracking-tight text-gray-900 dark:text-white',
        className
      )}
      {...props}
    >
      {children}
    </h3>
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-gray-500 dark:text-gray-400', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

export { Card };
